import ApiBase from './ApiBase';
import axios from 'axios';

import { getManyByChunk } from '../utils';
import { stringify } from '../components/forked/stringify';

/**
 * Investor API
 */
class InvestorApi extends ApiBase {
  /**
   * Check if investor ID already exists
   * @param {*} investorId
   */
  async checkIdExists(investorId) {
    const request = {
      url: `${this.API_URL}/investor?id=${investorId}`,
      headers: this.getHeaders(),
      method: 'HEAD',
      validateStatus: function(status) {
        return status === 200 || status === 403 || status === 404;
      }
    };

    const response = await axios.request(request);

    if (response.status === 200 || response.status === 403) {
      return true;
    } else if (response.status === 404) {
      return false;
    } else {
      throw Error('An error has occurred');
    }
  }

  /**
   * update investors
   * @param {*} investors
   */
  async update(investors) {
    const request = {
      url: `${this.API_URL}/investors`,
      headers: this.getHeaders(),
      method: 'POST',
      data: JSON.stringify(investors)
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Get related contacts in excel
   * @param investorId
   * @param {*} filters
   */
  async getRelatedContactsAsExcel(investorId, filters) {
    const queryParameters = stringify(
      {
        investorId: investorId,
        lastName: filters.lastName
      },
      {
        arrayFormat: 'comma'
      }
    );
    const request = {
      url: `${this.API_URL}/investor/contacts/export?${queryParameters}`,
      headers: this.getHeaders(),
      method: 'GET',
      responseType: 'blob'
    };

    const response = await axios.request(request);

    return response.data;
  }

  async validateImportFromExcel(payload) {
    const request = {
      url: `${this.API_URL}/investor/validateimport`,
      method: 'POST',
      headers: this.getHeaders(),
      data: payload
    };

    const response = await axios.request(request);
    return response.data;
  }

  async importInvestorsFromExcel(payload) {
    const request = {
      url: `${this.API_URL}/investor/importexcel`,
      method: 'POST',
      headers: this.getHeaders(),
      data: payload
    };

    const response = await axios.request(request);
    return response.data;
  }

  async downloadInvestorImportTemplate() {
    const request = {
      url: `${this.API_URL}/investor/import/template`,
      method: 'GET',
      headers: this.getHeaders(),
      responseType: 'blob'
    };

    const response = await axios.request(request);
    return response.data;
  }

  async exportInvestorsExcel(
    investorId,
    investorName,
    status,
    investorTagId,
    fundFamilyId
  ) {
    const queryParameters = stringify(
      {
        id: investorId,
        name: investorName,
        status: status,
        investorTagId: investorTagId,
        fundFamilyId: fundFamilyId
      },
      {
        arrayFormat: 'open-ic'
      }
    );

    const request = {
      url: `${this.API_URL}/investors/excel/export?${queryParameters}`,
      method: 'GET',
      headers: this.getHeaders(),
      responseType: 'blob'
    };

    const response = await axios.request(request);
    return response.data;
  }

  /**
   * update investors
   * @param investorId: string
   * @param primaryContacts [{documentTypeId: int, type: 'P', userId: string}]
   */
  async updatePrimaryContacts(investorId, primaryContacts) {
    const queryParameters = stringify({
      investorId: investorId
    });
    const request = {
      url: `${this.API_URL}/investor/primarycontacts?${queryParameters}`,
      headers: this.getHeaders(),
      method: 'POST',
      data: JSON.stringify(primaryContacts)
    };

    const response = await axios.request(request);

    return response.data;
  }

  async create(investorId, payload) {
    const request = {
      url: `${this.API_URL}/investor?id=${investorId}`,
      method: 'POST',
      headers: this.getHeaders(),
      data: payload
    };

    const response = await axios.request(request);
    return response.data;
  }

  /**
   * Update an existing investor with a PUT request
   */
  async updateInvestor(id, payload) {
    const request = {
      url: `${this.API_URL}/investor?id=${id}`,
      headers: this.getHeaders(),
      method: 'PUT',
      data: payload
    };

    const response = await axios.request(request);

    return response.data;
  }

  /**
   * Get investors by Ids with chunk (workaround because of getMany : false in IccAPi.js for investors)
   */
  async getInvestors(investorIds, paramsMaxSize) {
    const baseUrl = `${this.API_URL}/investors?orphanVisibility=ALL_VISIBLE&id=`;
    let encodedReqIds = encodeURI(`${investorIds}`);

    return getManyByChunk(baseUrl, encodedReqIds, paramsMaxSize);
  }

  async getInvestor(id) {
    const request = {
      url: `${this.API_URL}/investor?id=${id}`,
      headers: this.getHeaders(),
      method: 'GET'
    };

    const response = await axios.request(request);

    return response.data;
  }

  async getRelatedContacts(investorIds, paramsMaxSize) {
    const baseUrl = `${this.API_URL}/investor/contacts?orderBy=firstName&orderDirection=ASC&investorId=`;
    let encodedReqIds = encodeURI(`${investorIds}`);

    return getManyByChunk(baseUrl, encodedReqIds, paramsMaxSize);
  }
}

export default new InvestorApi();
